import { FiBook } from 'react-icons/fi';

export const numbersDatesTimeData = {
  courseId: 'numbers-dates-time',
  courseType: 'vocabulary',
  title: 'Numbers, Dates & Time',
  icon: FiBook,
  overview: {
    impact: 'Critical for Listening accuracy and Speaking precision',
    focus: 'Saying and understanding numbers, dates, and time expressions',
    uses: 'Listening all sections, Speaking Part 1',
    duration: '20 minutes'
  },
  sections: [
    {
      title: 'Numbers',
      content: `<p>Common number pitfalls in IELTS Listening:</p>`,
      points: [
        '13 vs 30: \'thirteen\' (stress on -teen) vs \'thirty\' (stress on thir-)',
        'Large numbers: 1,500 = \'one thousand five hundred\' or \'fifteen hundred\'',
        'Decimals: 3.5 = \'three point five\'',
        'Percentages: 45% = \'forty-five percent\'',
        'Fractions: ½ = \'a half\', ¼ = \'a quarter\', ¾ = \'three quarters\''
      ]
    },
    {
      title: 'Dates',
      content: `<p>Different formats to recognize:</p>`,
      points: [
        'British: 5th March 2024 (day first)',
        'American: March 5, 2024 (month first)',
        'Ordinal numbers: 1st first, 2nd second, 3rd third, 4th-31st: add -th',
        'Years: 2024 = \'twenty twenty-four\' or \'two thousand and twenty-four\''
      ]
    },
    {
      title: 'Time',
      content: `<p>Time expressions in IELTS:</p>`,
      points: [
        '9:15 = \'quarter past nine\' or \'nine fifteen\'',
        '9:30 = \'half past nine\' or \'nine thirty\'',
        '9:45 = \'quarter to ten\' or \'nine forty-five\'',
        'am/pm or \'in the morning/afternoon/evening\''
      ]
    }
  ],
  exercises: [
    {
      title: 'Write the Number You Hear',
      type: 'select',
      instructions: 'Choose the correct number.',
      questions: [
        {
          text: '\'The population is three point five million.\'',
          options: [
            '3,500,000',
            '3.5',
            '35,000'
          ],
          answer: '3,500,000',
          explanation: '3.5 million = 3,500,000.'
        },
        {
          text: '\'The appointment is on the thirteenth.\'',
          options: [
            '13th',
            '30th',
            '3rd'
          ],
          answer: '13th',
          explanation: '\'Thirteenth\' = 13th. Stress on \'-teen\'.'
        },
        {
          text: '\'It costs fifteen hundred dollars.\'',
          options: [
            '$1,500',
            '$1,050',
            '$15,000'
          ],
          answer: '$1,500',
          explanation: '\'Fifteen hundred\' = 1,500.'
        }
      ]
    },
    {
      title: 'Read the Number/Date',
      type: 'select',
      instructions: 'Choose the correct way to say or read the number, date, or time.',
      questions: [
        {
          text: 'How do you say 1,500?',
          options: ['one thousand five hundred / fifteen hundred', 'one hundred and fifty', 'fifteen thousand', 'one fifty'],
          answer: 'one thousand five hundred / fifteen hundred',
          explanation: '1,500 can be said as \'one thousand five hundred\' or informally \'fifteen hundred\'.'
        },
        {
          text: 'How do you say the date 3rd March?',
          options: ['the third of March', 'three March', 'March three', 'the thirdth of March'],
          answer: 'the third of March',
          explanation: 'Dates use ordinal numbers: \'the third of March\' or \'March the third\'.'
        },
        {
          text: 'How do you say 2:45?',
          options: ['quarter to three', 'quarter past two', 'half past two', 'two and three quarters'],
          answer: 'quarter to three',
          explanation: '2:45 = \'quarter to three\' (15 minutes before 3:00) or \'two forty-five\'.'
        },
        {
          text: 'How do you say 0.75?',
          options: ['nought point seven five', 'zero seventy-five', 'seven five', 'seventy-five point zero'],
          answer: 'nought point seven five',
          explanation: 'Decimals are read digit by digit after the point: \'nought point seven five\' or \'zero point seven five\'.'
        }
      ]
    },
    {
      title: 'Write Numbers in Words',
      type: 'fill-in',
      instructions: 'Write the number, date, or time in words.',
      questions: [
        {
          text: 'Write in words: 250',
          answer: 'two hundred and fifty',
          explanation: '250 = \'two hundred and fifty\'. In British English, \'and\' is used after \'hundred\'.'
        },
        {
          text: 'Write in words: 50%',
          answer: 'fifty percent',
          explanation: '50% = \'fifty percent\'. Percentages are common in IELTS Listening and Writing Task 1.'
        },
        {
          text: 'Write in words: 3/4',
          answer: 'three quarters',
          explanation: '3/4 = \'three quarters\'. Fractions: 1/2 = a half, 1/4 = a quarter, 3/4 = three quarters.'
        },
        {
          text: 'Write the year in words: 2024',
          answer: 'twenty twenty-four',
          explanation: '2024 = \'twenty twenty-four\' or \'two thousand and twenty-four\'. Both are correct.'
        }
      ]
    },
    {
      title: 'Practice Time Expressions',
      type: 'writing',
      instructions: 'Write a full sentence using the time expression given.',
      questions: [
        {
          text: 'Write a sentence using \'quarter past\' to describe when you start work or school.',
          answer: 'quarter past',
          explanation: 'Example: \'My classes start at quarter past eight every morning.\' \'Quarter past\' = 15 minutes after the hour.'
        },
        {
          text: 'Write a sentence describing an appointment using an ordinal date (e.g. 5th June).',
          answer: 'ordinal date',
          explanation: 'Example: \'My dentist appointment is on the twenty-first of April.\' Use ordinal numbers (first, second, third...) for dates.'
        },
        {
          text: 'Write a sentence using a large number (over 1,000) to describe a statistic or fact.',
          answer: 'large number',
          explanation: 'Example: \'The university has approximately twelve thousand five hundred students.\' Practise saying large numbers naturally for IELTS Listening.'
        }
      ]
    }
  ],
  tips: [
    'Practice distinguishing -teen (13) from -ty (30)',
    'Always check: did they say \'fifteen\' or \'fifty\'?',
    'Learn ordinal numbers for dates',
    'In Listening, numbers are high-frequency answer types'
  ],
  dailyPractice: [
    'Practice numbers, dates & time concepts daily',
    'Review and apply in IELTS practice tests',
    'Write example sentences using key concepts',
    'Teach the concept to someone else to reinforce understanding'
  ],
  backPath: '/exams/english/#vocabulary',
  backLabel: 'Back to Vocabulary',
  aiContext: 'Numbers, Dates & Time: Essential for IELTS Listening accuracy. Covers number distinctions, date formats, and time expressions.'
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...numbersDatesTimeData,
      title: numbersDatesTimeData.title + ' (Foundations)',
      overview: { ...numbersDatesTimeData.overview, duration: '15-20 minutes' },
      sections: numbersDatesTimeData.sections.slice(0, Math.max(2, Math.ceil(numbersDatesTimeData.sections.length / 2))),
      exercises: numbersDatesTimeData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...numbersDatesTimeData,
      title: numbersDatesTimeData.title + ' (Advanced)',
      overview: { ...numbersDatesTimeData.overview, duration: '30-35 minutes' },
      sections: numbersDatesTimeData.sections,
      exercises: numbersDatesTimeData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(numbersDatesTimeData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS tasks.',
      ],
    };
  }
  return numbersDatesTimeData;
}
